/**
 * ScenariosAdmin — /admin/szenarien (#1049, Polish #1050)
 * Notification-Events × Rollen-Mapping (m:n) gruppiert nach Kategorie + Sub-Modul.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import {
  Bell, ChevronDown, ChevronRight, Search, AlertCircle,
  Shield, Package, ShieldCheck, Handshake, BarChart3, Users, Eye, Building2, User,
} from 'lucide-react';

const API = '';

const ROLE_ICON_MAP = {
  Shield, Package, ShieldCheck, Handshake, BarChart3, Users, Eye, Building2, User,
};

function RoleIcon({ name, size = 14, color }) {
  const Cmp = ROLE_ICON_MAP[name] || Shield;
  return <Cmp size={size} color={color || 'currentColor'} />;
}

export default function ScenariosAdmin() {
  const { t, language } = useLanguage();
  const lang = language || 'de';
  const [scenarios, setScenarios] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [eventDomains, setEventDomains] = useState([]);
  const [roles, setRoles] = useState([]);
  const [collapsedKats, setCollapsedKats] = useState({});
  const [collapsedSubs, setCollapsedSubs] = useState({});
  const [filter, setFilter] = useState('');
  const [filterKategorie, setFilterKategorie] = useState('');
  const [savingEvent, setSavingEvent] = useState(null);
  const [toast, setToast] = useState(null);

  function load() {
    fetch(`${API}/api/admin/scenarios`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        setScenarios(d.scenarios || []);
        setKategorien(d.kategorien || []);
        setEventDomains(d.event_domains || d.modules || []);
      });
    fetch(`${API}/api/admin/roles`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setRoles(d.roles || []));
  }

  useEffect(load, []);

  const domainMap = useMemo(() => {
    const m = {};
    for (const d of eventDomains) m[d.modul_key || d.key] = d;
    return m;
  }, [eventDomains]);

  function roleLabel(r) {
    return lang === 'de' ? (r.label_de || r.rolle) : (r.label_en || r.label_de || r.rolle);
  }

  const grouped = useMemo(() => {
    const map = {};
    const f = filter.toLowerCase();
    for (const s of scenarios) {
      if (filterKategorie && s.kategorie !== filterKategorie) continue;
      if (f && !s.event_key.toLowerCase().includes(f) &&
          !(s.label_de || '').toLowerCase().includes(f) &&
          !(s.label_en || '').toLowerCase().includes(f)) continue;
      if (!map[s.kategorie]) map[s.kategorie] = { items: [], subgroups: {} };
      map[s.kategorie].items.push(s);
      const subKey = s.modul || '_misc';
      if (!map[s.kategorie].subgroups[subKey]) map[s.kategorie].subgroups[subKey] = [];
      map[s.kategorie].subgroups[subKey].push(s);
    }
    return map;
  }, [scenarios, filter, filterKategorie]);

  async function toggleRole(eventKey, rolle, currentlyAssigned) {
    const ev = scenarios.find(s => s.event_key === eventKey);
    if (!ev) return;
    const newRollen = currentlyAssigned
      ? ev.rollen.filter(r => r !== rolle)
      : [...new Set([...ev.rollen, rolle])];
    setSavingEvent(eventKey);
    try {
      const res = await fetch(`${API}/api/admin/scenarios/${encodeURIComponent(eventKey)}/roles`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rollen: newRollen }),
      });
      if (!res.ok) throw new Error('save_failed');
      setScenarios(prev => prev.map(s => s.event_key === eventKey ? { ...s, rollen: newRollen } : s));
    } catch {
      setToast({ type: 'error', msg: t('scenariosAdmin.saveFailed') });
      setTimeout(() => setToast(null), 3500);
    } finally { setSavingEvent(null); }
  }

  function toggleKat(key) {
    setCollapsedKats(prev => ({ ...prev, [key]: !prev[key] }));
  }
  function toggleSub(katKey, subKey) {
    setCollapsedSubs(prev => ({ ...prev, [`${katKey}::${subKey}`]: !prev[`${katKey}::${subKey}`] }));
  }

  function renderScenarioTable(items, keyPrefix) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[var(--bg-inset)] text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
              <th className="px-4 py-2 text-left font-semibold sticky left-0 bg-[var(--bg-inset)] z-20">{t('scenariosAdmin.event')}</th>
              {roles.map(r => (
                <th
                  key={r.rolle}
                  className="px-1.5 py-2 text-center font-semibold align-bottom"
                  style={{ minWidth: 84 }}
                  title={r.rolle}
                >
                  <div className="flex flex-col items-center gap-1">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md" style={{ background: `${r.color}15`, color: r.color }}>
                      <RoleIcon name={r.icon} size={14} />
                    </span>
                    <span className="text-[10px] font-semibold leading-tight text-[var(--text-secondary)] normal-case tracking-normal">
                      {roleLabel(r)}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-card)]/50">
            {items.map(s => (
              <tr key={`${keyPrefix}-${s.event_key}`} className={savingEvent === s.event_key ? 'opacity-50' : ''}>
                <td className="px-4 py-2 sticky left-0 bg-[var(--bg-card,#fff)] z-10">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium text-[var(--text-primary)] truncate">{lang === 'de' ? s.label_de : (s.label_en || s.label_de)}</span>
                        {s.kritisch && (
                          <span className="text-[9px] px-1 rounded bg-red-500/10 text-red-600 font-semibold flex items-center gap-0.5">
                            <AlertCircle size={9} />{t('scenariosAdmin.critical')}
                          </span>
                        )}
                        {s.customized_at && <span className="text-[9px] px-1 rounded bg-blue-500/10 text-blue-600 font-semibold">{t('scenariosAdmin.customized')}</span>}
                      </div>
                      <div className="text-[10px] text-[var(--text-tertiary)] font-mono truncate">{s.event_key}</div>
                    </div>
                  </div>
                </td>
                {roles.map(r => {
                  const assigned = (s.rollen || []).includes(r.rolle);
                  return (
                    <td key={r.rolle} className="px-2 py-2 text-center">
                      <button
                        onClick={() => toggleRole(s.event_key, r.rolle, assigned)}
                        disabled={savingEvent === s.event_key}
                        className={`w-5 h-5 rounded border transition-colors ${
                          assigned
                            ? 'bg-[var(--primary,#338edc)] border-[var(--primary,#338edc)]'
                            : 'bg-transparent border-[var(--border-card)] hover:border-[var(--primary,#338edc)]'
                        }`}
                        title={`${roleLabel(r)} — ${assigned ? t('scenariosAdmin.assigned') : t('scenariosAdmin.notAssigned')}`}
                      >
                        {assigned && <span className="text-white text-[11px] leading-none">✓</span>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="p-6 w-full max-w-screen-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[var(--primary,#338edc)]/10 flex items-center justify-center">
          <Bell size={20} className="text-[var(--primary,#338edc)]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('scenariosAdmin.title')}</h1>
          <p className="text-sm text-[var(--text-tertiary)]">{t('scenariosAdmin.subtitle')}</p>
        </div>
      </div>

      {/* Filter-Bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder={t('scenariosAdmin.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg text-sm focus:outline-none focus:border-[var(--primary,#338edc)]"
          />
        </div>
        <select
          value={filterKategorie}
          onChange={e => setFilterKategorie(e.target.value)}
          className="px-3 py-2 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg text-sm focus:outline-none"
        >
          <option value="">{t('scenariosAdmin.allCategories')}</option>
          {kategorien.map(k => <option key={k.key} value={k.key}>{lang === 'de' ? k.label_de : k.label_en}</option>)}
        </select>
        <span className="text-xs text-[var(--text-tertiary)]">
          {Object.values(grouped).reduce((s, a) => s + a.items.length, 0)} / {scenarios.length}
        </span>
      </div>

      {kategorien.map(kat => {
        const group = grouped[kat.key];
        if (!group || !group.items.length) return null;
        const isCollapsed = collapsedKats[kat.key];
        const subKeys = Object.keys(group.subgroups);
        const useSubgroups = subKeys.length >= 3 && group.items.length >= 15;

        return (
          <div key={kat.key} className="border border-[var(--border-card)] rounded-xl bg-[var(--bg-card,#fff)] mb-4 overflow-hidden">
            <button
              onClick={() => toggleKat(kat.key)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--bg-inset)] transition-colors"
            >
              <div className="flex items-center gap-2">
                {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                <span className="text-sm font-semibold text-[var(--text-primary)]">{lang === 'de' ? kat.label_de : kat.label_en}</span>
                <span className="text-[10px] text-[var(--text-tertiary)]">({group.items.length})</span>
              </div>
            </button>
            {!isCollapsed && (
              <div className="border-t border-[var(--border-card)]">
                {useSubgroups ? (
                  subKeys.map(subKey => {
                    const subItems = group.subgroups[subKey];
                    const subDomain = domainMap[subKey];
                    const subLabel = subDomain
                      ? (lang === 'de' ? subDomain.label_de : (subDomain.label_en || subDomain.label_de))
                      : (subKey === '_misc' ? t('scenariosAdmin.misc') : (subKey.charAt(0).toUpperCase() + subKey.slice(1)));
                    const subCollapsedKey = `${kat.key}::${subKey}`;
                    const subCollapsed = collapsedSubs[subCollapsedKey];
                    return (
                      <div key={subKey} className="border-b border-[var(--border-card)]/40 last:border-b-0">
                        <button
                          onClick={() => toggleSub(kat.key, subKey)}
                          className="w-full flex items-center gap-2 px-4 py-2 bg-[var(--bg-inset)]/50 hover:bg-[var(--bg-inset)] transition-colors"
                        >
                          {subCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                          <span className="text-xs font-semibold text-[var(--text-secondary)]">{subLabel}</span>
                          <span className="text-[10px] text-[var(--text-tertiary)]">({subItems.length})</span>
                        </button>
                        {!subCollapsed && renderScenarioTable(subItems, `${kat.key}-${subKey}`)}
                      </div>
                    );
                  })
                ) : renderScenarioTable(group.items, kat.key)}
              </div>
            )}
          </div>
        );
      })}

      {toast && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-sm font-medium z-50 ${
          toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
